export const getPlatform = () => {
  if (typeof navigator === "undefined") {
    return { isIOS: false, isAndroid: false, isMobile: false, isWeb: true };
  }

  const userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    ((window as any).opera as string) ||
    "";
  const platform = (navigator.platform || "").toLowerCase();
  const maxTouchPoints = (navigator as any).maxTouchPoints || 0;

  const isIOS =
    /ipad|iphone|ipod/.test(userAgent.toLowerCase()) ||
    (platform === "macintel" && maxTouchPoints > 1);

  const isAndroid = /android/.test(userAgent.toLowerCase());
  const isMobile = isIOS || isAndroid || /mobile/.test(userAgent.toLowerCase());

  return { isIOS, isAndroid, isMobile, isWeb: !isMobile };
};

export const safeEncodeURI = (value: string): string => {
  if (!value) return "";

  try {
    return encodeURIComponent(value);
  } catch (error) {
    return value.replace(
      /[^a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]/g,
      (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`
    );
  }
};

export const convertToAndroidIntent = (
  deepLink: string,
  fallbackUrl?: string
): string => {
  if (!deepLink.includes("://")) return deepLink;

  const [scheme, rest] = deepLink.split("://");

  const intentSupportedSchemes: Record<string, string> = {
    whatsapp: "com.whatsapp",
    telegram: "org.telegram.messenger",
    tg: "org.telegram.messenger",
    instagram: "com.instagram.android",
    twitter: "com.twitter.android",
    fb: "com.facebook.katana",
    facebook: "com.facebook.katana",
    youtube: "com.google.android.youtube",
  };

  const packageName = intentSupportedSchemes[scheme.toLowerCase()];

  if (packageName && fallbackUrl) {
    const intentUrl = `intent://${rest}#Intent;scheme=${scheme};package=${packageName};S.browser_fallback_url=${encodeURIComponent(
      fallbackUrl
    )};end`;
    return intentUrl;
  }

  return deepLink;
};

export const getPlatformOptimizedLink = (
  deepLink: string,
  fallbackLink?: string
): { primary: string; fallback?: string } => {
  const { isIOS, isAndroid } = getPlatform();
  const sanitizedFallback = fallbackLink?.trim();

  if (isAndroid && deepLink.includes("://")) {
    if (sanitizedFallback && sanitizedFallback.startsWith("http")) {
      const intentLink = convertToAndroidIntent(deepLink, sanitizedFallback);
      return { primary: sanitizedFallback, fallback: intentLink };
    }
    const intentLink = convertToAndroidIntent(deepLink, sanitizedFallback);
    return { primary: intentLink, fallback: sanitizedFallback };
  }

  if (isIOS) {
    if (sanitizedFallback && sanitizedFallback.startsWith("http")) {
      return { primary: sanitizedFallback, fallback: deepLink };
    }
    return { primary: deepLink, fallback: sanitizedFallback };
  }

  return { primary: deepLink, fallback: sanitizedFallback };
};

export const handleDeepLinkClick = (
  deepLink: string,
  fallbackLink?: string,
  onSuccess?: () => void,
  onFallback?: () => void
): void => {
  const { primary, fallback } = getPlatformOptimizedLink(
    deepLink,
    fallbackLink
  );
  const usableFallback =
    fallback && fallback !== primary ? fallback : undefined;

  let appOpened = false;
  const handleVisibilityChange = () => {
    if (document.hidden) {
      appOpened = true;
      onSuccess?.();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  const fallbackTimeout = setTimeout(() => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);

    if (!appOpened && usableFallback) {
      onFallback?.();
      window.location.href = usableFallback;
    }
  }, 2500);

  window.location.href = primary;

  window.addEventListener(
    "blur",
    () => {
      clearTimeout(fallbackTimeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    },
    { once: true }
  );
};

export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") return false;

  try {
    new URL(url);
    return true;
  } catch {
    const customSchemePattern = /^[a-z][a-z0-9+.-]*:\/\/.+/i;
    return customSchemePattern.test(url);
  }
};

export const extractProtocol = (url: string): string => {
  if (!url) return "";
  const match = url.match(/^([a-z][a-z0-9+.-]*):/i);
  return match ? match[1].toLowerCase() : "";
};

export const isDeepLink = (url: string): boolean => {
  const protocol = extractProtocol(url);
  const webProtocols = ["http", "https", "ftp", "ftps"];
  return protocol !== "" && !webProtocols.includes(protocol);
};
